import { useCallback, useEffect, useState } from "react";
import { convertToSubcriberDate } from "../utils/helper";
import "./reported-issue-details.css";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  AUTO,
  BASE_URL,
  CATEGORY_ID_MAP,
  CHILD,
  CP,
  FOOD,
  MEDICINE,
  PRESS
} from "../assets/constant";
import { AutoComponent } from "../components/reported-issue/auto";
import CommentBox from "../components/reported-issue/comment-box";
import { FoodAndMedicineComponent } from "../components/reported-issue/foodAndMedicine";
import { PressComponent } from "../components/reported-issue/press";
import { ChildAndCPComponent } from "../components/reported-issue/childAndCP";
import { ShareOptions } from "../components/share-options";
import { Modal, Button, Form, Spinner } from "react-bootstrap";

export const ReportedIssueDetails = () => {
  const [recallDetails, setRecallDetails] = useState({});
  const [issueDetails, setIssueDetails] = useState({});
  const [comments, setComments] = useState([]);
  const [companyType, setCompanyType] = useState("");
  const [shareOption, setShareOption] = useState(false);
  const [userId, setUserId] = useState(null);
  const [recallId, setRecallId] = useState(null);
  
  // Signature states
  const [signatures, setSignatures] = useState([]);
  const [isLoadingSignatures, setIsLoadingSignatures] = useState(false);
  const [imageErrors, setImageErrors] = useState({});
  
  // Edit state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingComment, setEditingComment] = useState(null);
  const [editedCommentText, setEditedCommentText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Delete state
  const [deletingCommentId, setDeletingCommentId] = useState(null);

  const user = useSelector((state) => state.auth);

  const handleRedirect = () => {
    window.history.back();
  };

  // Fetch signatures for the recall
  const fetchSignatures = useCallback(async () => {
    if (!userId || !recallId) {
      return;
    }

    setIsLoadingSignatures(true);
    try {
      const url = `${BASE_URL}/signature?userId=${userId}&recallId=${recallId}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${user?.token}` }
      });

      if (response.data?.success) {
        setSignatures(response.data.data || []);
        setImageErrors({});
      }
    } catch (error) {
      console.error('Error fetching signatures:', error);
    } finally {
      setIsLoadingSignatures(false);
    }
  }, [userId, recallId, user?.token]);

  const fetchDetails = useCallback(async () => {
    try {
      const href = window.location.href;
      const [type, recallIdFromUrl, id] = href.split("/").slice(-3);
      setCompanyType(type);
      setRecallId(recallIdFromUrl);

      // Get user ID from auth
      if (user?.user?.id) {
        setUserId(user.user.id);
      }

      // Fetch recall details
      const response = await axios.post(
        `${BASE_URL}/recall/details`,
        {
          type: CATEGORY_ID_MAP[type],
          recallId: recallIdFromUrl
        },
        {
          headers: {
            Authorization: `Bearer ${user?.token}`
          }
        }
      );
      console.log('Recall Details:', response?.data?.data?.[0]);
      setRecallDetails(response?.data?.data?.[0]);

      // Fetch issue details
      const issueResponse = await axios.get(`${BASE_URL}/issue/details/${id}`, {
        headers: {
          Authorization: `Bearer ${user?.token}`
        }
      });
      console.log('Issue Details:', issueResponse?.data?.data?.[0]);
      setIssueDetails(issueResponse?.data?.data?.[0]);

      // Fetch comments
      await fetchComments(id);
    } catch (error) {
      console.error(error);
    }
  }, [user?.token, user?.user?.id]);

  const fetchComments = async (issueId) => {
    try {
      const id = issueId || issueDetails?.id;
      if (!id) return;
      
      const commentResponse = await axios.post(
        `${BASE_URL}/recall/getcomments`,
        {
          issueId: id
        },
        {
          headers: {
            Authorization: `Bearer ${user?.token}`
          }
        }
      );
      setComments(commentResponse?.data?.data || []);
    } catch (error) {
      console.error("Error fetching comments:", error);
    }
  };

  const updateComment = (newComment) => {
    setComments((prevComments) => [newComment, ...prevComments]);
  };

  // Handle Edit Comment - Using POST method (as API expects)
  const handleEditClick = (comment) => {
    setEditingComment(comment);
    setEditedCommentText(comment.comment);
    setShowEditModal(true);
  };

  const handleEditSubmit = async () => {
    if (!editedCommentText.trim()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${BASE_URL}/recall/editComments`,
        {
          comment_id: editingComment.comment_id,
          new_comment: editedCommentText.trim()
        },
        {
          headers: {
            Authorization: `Bearer ${user?.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response?.data?.success) {
        // Update the comment in the list
        setComments(prevComments =>
          prevComments.map(comment =>
            comment.comment_id === editingComment.comment_id
              ? { ...comment, comment: editedCommentText.trim() }
              : comment
          )
        );
        setShowEditModal(false);
        setEditingComment(null);
        setEditedCommentText("");
      } else {
        console.error("Edit failed:", response?.data?.message);
      }
    } catch (error) {
      console.error("Error editing comment:", error);
      if (error.response?.status === 401) {
        console.error("Unauthorized - Please check your authentication");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditCancel = () => {
    setShowEditModal(false);
    setEditingComment(null);
    setEditedCommentText("");
  };

  // Handle Delete Comment - Using DELETE method
  const handleDeleteComment = async (commentId) => {
    if (!commentId) return;

    setDeletingCommentId(commentId);
    try {
      const response = await axios({
        method: 'DELETE',
        url: `${BASE_URL}/recall/deleteComments`,
        data: {
          comment_id: commentId
        },
        headers: {
          Authorization: `Bearer ${user?.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response?.data?.success) {
        // Remove the comment from the list
        setComments(prevComments =>
          prevComments.filter(comment => comment.comment_id !== commentId)
        );
      } else {
        console.error("Delete failed:", response?.data?.message);
      }
    } catch (error) {
      console.error("Error deleting comment:", error);
      if (error.response?.status === 401) {
        console.error("Unauthorized - Please check your authentication");
      }
    } finally {
      setDeletingCommentId(null);
    }
  };

  // Image error handler for signatures
  const handleImageError = (signatureId) => {
    setImageErrors(prev => ({
      ...prev,
      [signatureId]: true
    }));
  };

  // Format date helper
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  // Fetch signatures when userId and recallId are available
  useEffect(() => {
    if (userId && recallId) {
      fetchSignatures();
    }
  }, [userId, recallId, fetchSignatures]);

  return (
    <div className="subscriber-container">
      <div className="reported-issue-details-container">
        <div className="reported-issue-details-header">
          <span className="reported-issue-details-title">
            <button
              className="fa-solid fa-arrow-left"
              style={{
                cursor: "pointer",
                background: "none",
                border: "none",
                fontSize: "14px"
              }}
              onClick={handleRedirect}
              aria-label="Go back"
            />
            <h4>{recallDetails?.Title || recallDetails?.recall_title || issueDetails?.subject || issueDetails?.description || 'No title available'}</h4>
          </span>
          <span>
            <button
              className="fa fa-share-from-square"
              onClick={() => setShareOption(!shareOption)}
              style={{
                cursor: "pointer",
                background: "none",
                border: "none"
              }}
            />
            {shareOption && (
              <span
                style={{
                  display: "flex",
                  flexWrap: "nowrap",
                  gap: "10px",
                  background: "white",
                  padding: "10px",
                  borderRadius: "10px",
                  marginTop: "10px"
                }}
              >
                <ShareOptions />
              </span>
            )}
          </span>
        </div>

        {/* Pass signatures to all category components */}
        {companyType === AUTO && (
          <AutoComponent
            recallDetails={recallDetails}
            issueDetails={issueDetails}
            signatures={signatures}
            isLoadingSignatures={isLoadingSignatures}
            onImageError={handleImageError}
            imageErrors={imageErrors}
            formatDate={formatDate}
          />
        )}
        {(companyType === FOOD || companyType === MEDICINE) && (
          <FoodAndMedicineComponent
            recallDetails={recallDetails}
            issueDetails={issueDetails}
            signatures={signatures}
            isLoadingSignatures={isLoadingSignatures}
            onImageError={handleImageError}
            imageErrors={imageErrors}
            formatDate={formatDate}
          />
        )}
        {companyType === PRESS && (
          <PressComponent
            recallDetails={recallDetails}
            issueDetails={issueDetails}
            signatures={signatures}
            isLoadingSignatures={isLoadingSignatures}
            onImageError={handleImageError}
            imageErrors={imageErrors}
            formatDate={formatDate}
          />
        )}
        {(companyType === CP || companyType === CHILD) && (
          <ChildAndCPComponent
            recallDetails={recallDetails}
            issueDetails={issueDetails}
            signatures={signatures}
            isLoadingSignatures={isLoadingSignatures}
            onImageError={handleImageError}
            imageErrors={imageErrors}
            formatDate={formatDate}
          />
        )}

        <CommentBox user={user} updateComment={updateComment} />

        <div className="comment-container">
          {comments?.map((comment) => (
            <div className="comment-content" key={comment?.comment_id}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>
                  <i
                    style={{ fontSize: "14px", paddingRight: "10px" }}
                    className="fa fa-user"
                  />
                  <span style={{ fontSize: "14px", fontWeight: "bold" }}>
                    {comment?.username}
                  </span>
                </span>
                <span style={{ fontSize: "12px" }}>
                  {convertToSubcriberDate(comment?.createdAt || new Date())}
                </span>
              </div>

              <div className="comment-message-row">
                <span className="comment-message">{comment?.comment}</span>

                <div className="comment-action-icons">
                  <i
                    className="fa-regular fa-pen-to-square edit-icon"
                    title="Edit"
                    onClick={() => handleEditClick(comment)}
                  />
                  <i
                    className="fa-regular fa-trash-can delete-icon"
                    title="Delete"
                    onClick={() => handleDeleteComment(comment.comment_id)}
                  />
                  {deletingCommentId === comment.comment_id && (
                    <span className="ms-1">
                      <i className="fas fa-spinner fa-spin" />
                    </span>
                  )}
                </div>
              </div>

              {(() => {
                const attachment = comment?.attachment;
                let parsedAttachment = [];
                try {
                  parsedAttachment = attachment ? JSON.parse(attachment) : [];
                } catch (e) {
                  parsedAttachment = [];
                }
                const isAttachment = parsedAttachment?.length > 0;
                if (!isAttachment) {
                  return null;
                }
                const attachmentUrl = parsedAttachment[0]?.url;
                const attachmentName = parsedAttachment[0]?.filename;
                return (
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginTop: "10px"
                    }}
                  >
                    <span style={{ fontSize: "12px" }}>
                      {attachmentName || "Attachment"}
                    </span>
                    <a
                      href={attachmentUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{ textDecoration: "none", paddingLeft: "10px" }}
                    >
                      <i className="fa fa-download" />
                    </a>
                  </span>
                );
              })()}
            </div>
          ))}
        </div>
      </div>

      {/* Edit Comment Modal */}
      <Modal show={showEditModal} onHide={handleEditCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Comment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Comment</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={editedCommentText}
                onChange={(e) => setEditedCommentText(e.target.value)}
                placeholder="Edit your comment..."
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleEditCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleEditSubmit}
            disabled={isSubmitting || !editedCommentText.trim()}
          >
            {isSubmitting ? "Saving..." : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};